import {
  addMissingValueInParams, type MemberInformation, memberListFilter, type MemberFilterRequest,
  type Person,
  type PersonBaseBasicInformation,
  type PersonFilterRequest,
  personListFilter
} from 'services'
import React, { useEffect, useRef, useState } from 'react'
import { Box, Card, CardContent, CircularProgress, FormControl, Grid, Stack, TextField, IconButton, Tooltip } from '@mui/material'
import { AlertDialog, PersonCard, PersonDetailsPopover, useCustomBehaviourLuts, useUsPrefixLuts } from 'components'
import { StyledCard, StyledCircularProgress, StyledStack } from './personAutoCompleteStyles'
import { missingValueFilters, usPeopleListFilter } from 'services'
import { generateLegalName, isEmptyLastNameAllowed } from 'helpers'
import InfoIcon from '@mui/icons-material/Info'
type PersonType = 'member' | 'personUS' | 'personInternational'

export interface PersonAutocompleteProps<T extends Person | PersonBaseBasicInformation | MemberInformation> {
  person?: T
  updatePerson: (newPerson: T) => void
  filledPerson: boolean
  editable: boolean
  editing: boolean
  countryId?: number | null
  setEditing: (value: boolean) => void
  createPerson: boolean
  setCreatePerson: (value: boolean) => void
  personType?: PersonType
  memberFilterOptions?: { chamberIds?: number[], majmin?: string, stateId?: number }
}
export const PersonAutocomplete = <T extends Person | PersonBaseBasicInformation | MemberInformation>({
  person,
  updatePerson,
  filledPerson,
  editable,
  editing,
  setEditing,
  createPerson,
  setCreatePerson,
  countryId,
  personType = 'personInternational',
  memberFilterOptions
}: PersonAutocompleteProps<T>): React.ReactElement => {
  const [loading, setLoading] = useState(false)
  const [peopleResults, setPeopleResults] = useState<T[]>([])
  const timeoutRef = useRef<NodeJS.Timeout | undefined>()
  const [firstName, setFirstName] = useState((person as MemberInformation)?.person?.firstName ?? (person as PersonBaseBasicInformation | Person)?.firstName ?? '')
  const [lastName, setLastName] = useState((person as MemberInformation)?.person?.lastName ?? (person as PersonBaseBasicInformation | Person)?.lastName ?? '')
  const [showFirstOption, setShowFirstOption] = useState(false)
  const { lutValues: customBehaviourValues } = useCustomBehaviourLuts()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null)

  const updateResults = (isSearchById: boolean): void => {
    setLoading(true)
    const isMemberType = personType === 'member'
    const isPersonType = personType === 'personInternational'

    const baseParams: PersonFilterRequest = {
      first_name: isNaN(Number(firstName)) ? firstName : '',
      last_name: lastName,
      id: !isNaN(Number(firstName)) ? firstName : '',
      page: 1,
      order_by: 'ID',
      order_direction: 'ASC'
    }
    if (lastName === '' && !isSearchById) {
      addMissingValueInParams(baseParams, missingValueFilters.LastName)
    }

    const params: MemberFilterRequest | PersonFilterRequest = isMemberType
      ? {
          ...baseParams,
          'chamber[]': memberFilterOptions?.chamberIds,
          majmin: memberFilterOptions?.majmin,
          state: memberFilterOptions?.stateId
        }
      : baseParams

    const filterFunction = isMemberType ? memberListFilter : (isPersonType ? personListFilter : usPeopleListFilter)
    void filterFunction(params as MemberFilterRequest).then((results) => {
      setLoading(false)
      setPeopleResults(results.data as T[])
      setShowFirstOption(!isSearchById && !isMemberType)
    })
  }
  // Clear firstName and lastName when not editing
  useEffect(() => {
    if (!editing && (firstName !== '' || lastName !== '')) {
      setFirstName('')
      setLastName('')
    }
  }, [editing])

  useEffect(() => {
    const isSearchById = !isNaN(Number(firstName)) && lastName === ''
    const validQuery = firstName.length >= 2 && (lastName.length >= 2 || isSearchById || isEmptyLastNameAllowed(customBehaviourValues, countryId))
    if (validQuery && !createPerson && editing) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => { updateResults(isSearchById) }, 500)
    } else {
      setPeopleResults([])
      setShowFirstOption(false)
    }
  }, [firstName, lastName, editing, createPerson, customBehaviourValues, countryId, memberFilterOptions])

  const handleOutsideClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement
    const isClickInsideDialog = target.closest('.MuiDialog-root') !== null

    if (!isClickInsideDialog && (dropdownRef.current != null) && !dropdownRef.current.contains(target)) {
      setShowFirstOption(false)
      setPeopleResults([])
    }
  }

  useEffect(() => {
    if (editing) {
      document.addEventListener('click', handleOutsideClick)
    } else {
      document.removeEventListener('click', handleOutsideClick)
    }

    return () => {
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [editing])

  const handleCardClick = (value: T): void => {
    updatePerson(value)
    setEditing(false)
  }

  const handleCreateNewClick = (): void => {
    lastName === '' ? setOpenDialog(true) : createNewPerson()
  }

  const isBasicPerson = (person?: T): person is T & (Person | PersonBaseBasicInformation) => {
    return person == null || !('legrole' in person)
  }

  const createNewPerson = (): void => {
    if (!isBasicPerson(person)) return
    const updatedPerson = { ...person, firstName, lastName }
    updatePerson(updatedPerson)
    setCreatePerson(true)
  }

  const handleDialogClose = (): void => {
    setOpenDialog(false)
  }

  const handleDialogConfirm = (): void => {
    createNewPerson()
    setOpenDialog(false)
  }

  const handleInfoClose = (event?: React.SyntheticEvent): void => {
    if (event != null) {
      event.stopPropagation()
    }
    setInfoAnchorEl(null)
    setSelectedPersonId(null)
  }

  const handleEdit = (): void => {
    setEditing(true)
  }

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>, personId: number): void => {
    event.stopPropagation()
    setInfoAnchorEl(event.currentTarget)
    setSelectedPersonId(personId)
  }

  if (!editing) {
    const cardPerson = (person as MemberInformation)?.person ?? (person as PersonBaseBasicInformation | Person)

    return <PersonCard person={cardPerson} handleEdit={handleEdit} editable={editable} filledPerson={filledPerson}/>
  }

  return (
      <Grid container spacing={1}>
          <Grid item xs={5}>
              <FormControl fullWidth>
                  <TextField name={'firstName'} label={'First Name'} value={firstName} onChange={(event => { setFirstName(event.target.value) }) }/>
              </FormControl>
          </Grid>
          <Grid item xs={5}>
              <FormControl fullWidth>
                  <TextField name={'lastName'} label={'Last Name'} value={lastName} onChange={(event => { setLastName(event.target.value) }) }/>
              </FormControl>
          </Grid>
          {!createPerson &&
          <Grid item flex={1} xs={11}>
            {loading && <Box sx={StyledCircularProgress}><CircularProgress/></Box>}
              {!loading &&
              <Stack ref={dropdownRef} spacing={1} sx={ StyledStack }>
                  {showFirstOption && <Card
                      key='Add New'
                      sx={StyledCard}
                      onClick={() => { handleCreateNewClick() }}
                  >
                      <CardContent>
                          Add New Person
                      </CardContent>
                  </Card>}
                  {peopleResults.map((value) => {
                    const person = (value as MemberInformation)?.person ?? (value as PersonBaseBasicInformation | Person)
                    return (
                      <Card
                          key={person.id}
                          sx={StyledCard}
                          onClick={() => { handleCardClick(value) }}
                      >
                          <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                {personType === 'personInternational' && `${String(person.id)} - ${person.firstName ?? ''} ${person.middleName ?? ''} ${person.lastName}`}
                                {personType !== 'personInternational' && `${String(person.id)} - ${generateLegalName(person, usPrefixLutValues.prefixes)}`}

                              </div>
                              {personType !== 'personInternational' && (
                                <Tooltip title="View more information">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { handleInfoClick(e, person.id) }}
                                    color="primary"
                                  >
                                    <InfoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </CardContent>
                      </Card>
                    )
                  })}
              </Stack>
              }
          </Grid>
          }
        <AlertDialog
          open={openDialog}
          title="Confirm Empty Last Name"
          contentText="You are saving this record with a single name. Are you sure you want to do this?"
          nameButtonCancel="No"
          nameButtonConfirm="Yes"
          onClose={handleDialogClose}
          onButtonClick={handleDialogConfirm}
        />
        {(selectedPersonId != null) && (
          <PersonDetailsPopover
            personId={selectedPersonId}
            anchorEl={infoAnchorEl}
            onClose={handleInfoClose}
          />
        )}
      </Grid>
  )
}
